import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import CustomConfirmationModal from '../../../components/CustomConfirmationModal'
import NetworksForm from './NetworksForm'
import FormModal from '../../FormModal'
import MiniSwitch from '../../formUtils/MiniSwitch'
import { useApi } from '../../../hooks/useApi'
import { convertWeiToGwei, convertGweiToWei, shortenAddress } from '../../../utils/web3-utils'
import toast from 'react-hot-toast'

const toOptionalString = (value?: string): string | undefined => (value ? value : undefined)

interface RelayerData {
  _id: string
  chainId: number
  denied: boolean
  name: string
  symbol: string
  subgraphUrl?: string
  subgraphId?: string
  subgraphDeploymentId?: string
  goldskySubgraphUrl?: string
  thresholdAmount: number
  gasThresholdAmount: number
  executorGasLimitBufferPct: number
  transactionDelaySeconds: number
  transactionsExpirationSeconds: number
  missingTxMinutesTimeout: number
  minimumMinutesBetweenExecutions: number
  maxPriorityFeePerGas?: string
  inactiveMinutesToNotify: number
  executorBalanceThresholdUsd: number
  explorerUrl: string
}

const NetworksPage: React.FC = () => {
  const { callApi } = useApi()
  const [relayerData, setRelayerData] = useState<RelayerData[] | null>(null)
  const [deleteParams, setDeleteParams] = useState<RelayerData | null>(null)
  const [editItem, setEditItem] = useState<RelayerData | null>(null)
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [editedProps, setEditedProps] = useState<RelayerData | null>(null)
  const [addNew, setAddNew] = useState(false)

  const fetchRelayerData = async () => {
    try {
      const response = await callApi<RelayerData[]>('/relayer-executor/chains')
      setRelayerData(response)
    } catch (e: any) {
      console.error('Networks error:', e)
    }
  }

  useEffect(() => { fetchRelayerData() }, [])

  const handleConfirmDelete = async () => {
    if (!deleteParams) return
    try {
      await callApi(`/relayer-executor/chains/${deleteParams.chainId}`, {}, 'DELETE')
      fetchRelayerData()
    } catch (e: any) {
      console.error('Error deleting network:', e)
    }
    setDeleteParams(null)
  }

  const handleEdit = (item: RelayerData, index: number) => {
    setEditIndex(index)
    setEditItem(item)
    setEditedProps({ ...item, gasThresholdAmount: convertWeiToGwei(item.gasThresholdAmount) as any })
  }

  const handleSaveEdit = async () => {
    try {
      await callApi(
        `/relayer-executor/chains/${editItem!.chainId}`,
        {
          chainId: editedProps?.chainId,
          name: editedProps?.name,
          symbol: editedProps?.symbol,
          denied: editedProps?.denied,
          subgraphUrl: toOptionalString(editedProps?.subgraphUrl),
          subgraphId: toOptionalString(editedProps?.subgraphId),
          subgraphDeploymentId: toOptionalString(editedProps?.subgraphDeploymentId),
          goldskySubgraphUrl: toOptionalString(editedProps?.goldskySubgraphUrl),
          thresholdAmount: parseInt(String(editedProps?.thresholdAmount)),
          gasThresholdAmount: convertGweiToWei(parseInt(String(editedProps?.gasThresholdAmount))),
          executorGasLimitBufferPct: parseInt(String(editedProps?.executorGasLimitBufferPct)),
          transactionDelaySeconds: parseInt(String(editedProps?.transactionDelaySeconds)),
          transactionsExpirationSeconds: parseInt(String(editedProps?.transactionsExpirationSeconds)),
          missingTxMinutesTimeout: parseInt(String(editedProps?.missingTxMinutesTimeout)),
          maxPriorityFeePerGas: editedProps?.maxPriorityFeePerGas,
          minimumMinutesBetweenExecutions: parseInt(String(editedProps?.minimumMinutesBetweenExecutions)),
          inactiveMinutesToNotify: parseInt(String(editedProps?.inactiveMinutesToNotify)),
          executorBalanceThresholdUsd: parseInt(String(editedProps?.executorBalanceThresholdUsd)),
          explorerUrl: editedProps?.explorerUrl,
        },
        'PUT'
      )
      fetchRelayerData()
    } catch (e: any) {
      console.error('Error saving network:', e)
    }
    setEditIndex(null)
  }

  if (!relayerData) return <LoaderBar />

  return (
    <Wrap>

      <Header>
        <AddBtn onClick={() => setAddNew(!addNew)}>
          {addNew ? 'Hide form' : '+ Add network'}
        </AddBtn>
      </Header>

      {addNew && (
        <FormModal onConfirm={() => { fetchRelayerData(); setAddNew(false) }} onCancel={() => setAddNew(false)}>
          <NetworksForm onSuccess={() => { fetchRelayerData(); setAddNew(false) }} />
        </FormModal>
      )}

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>ID</Th>
              <Th>Name</Th>
              <Th>Symbol</Th>
              <Th>Denied</Th>
              <Th>Subgraph</Th>
              <Th>Subgraph ID</Th>
              <Th>Subgraph Deployment ID</Th>
              <Th>Goldsky Subgraph</Th>
              <Th>Threshold</Th>
              <Th>Gas Threshold</Th>
              <Th>Gas Limit Buffer</Th>
              <Th>Tx Delay</Th>
              <Th>Tx Expiration</Th>
              <Th>Missing Timeout</Th>
              <Th>Min Between Exec</Th>
              <Th>Max Priority Fee</Th>
              <Th>Inactive Notify</Th>
              <Th>Balance Threshold</Th>
              <Th>Explorer URL</Th>
              <Th></Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {relayerData.map((item, index) => {
              const isEditing = editIndex === index
              const ep = editedProps

              const field = (
                key: keyof RelayerData,
                type: 'text' | 'number' = 'text',
                cls?: string
              ) =>
                isEditing ? (
                  <InlineInput
                    type={type}
                    style={cls === 'wide' ? { width: 200 } : cls === 'xwide' ? { width: 300 } : { width: 80 }}
                    value={String(ep?.[key] ?? '')}
                    onChange={(e) => setEditedProps({ ...ep!, [key]: e.target.value })}
                  />
                ) : null

              return (
                <tr key={item._id}>
                  <Td><Mono>{item.chainId}</Mono></Td>
                  <Td>{isEditing ? field('name', 'text', 'wide') : item.name}</Td>
                  <Td>{isEditing ? field('symbol', 'text') : <Mono>{item.symbol}</Mono>}</Td>
                  <Td>
                    {isEditing ? (
                      <MiniSwitch
                        ison={ep!.denied}
                        onToggle={() => setEditedProps({ ...ep!, denied: !ep!.denied })}
                      />
                    ) : item.denied ? <DeniedBadge>Denied</DeniedBadge> : <Muted>—</Muted>}
                  </Td>
                  <Td>{isEditing ? field('subgraphUrl', 'text', 'xwide') : <SubUrl>{item.subgraphUrl}</SubUrl>}</Td>
                  <Td>{isEditing ? field('subgraphId', 'text') : <Mono>{shortenAddress(item.subgraphId || '')}</Mono>}</Td>
                  <Td>{isEditing ? field('subgraphDeploymentId', 'text') : <Mono>{shortenAddress(item.subgraphDeploymentId || '')}</Mono>}</Td>
                  <Td>{isEditing ? field('goldskySubgraphUrl', 'text', 'xwide') : <SubUrl>{item.goldskySubgraphUrl || '—'}</SubUrl>}</Td>
                  <Td>{isEditing ? field('thresholdAmount', 'number') : <Mono>${item.thresholdAmount}</Mono>}</Td>
                  <Td>{isEditing ? field('gasThresholdAmount', 'number') : <Mono>{convertWeiToGwei(item.gasThresholdAmount)} gwei</Mono>}</Td>
                  <Td>{isEditing ? field('executorGasLimitBufferPct', 'number') : <Mono>{item.executorGasLimitBufferPct}%</Mono>}</Td>
                  <Td>{isEditing ? field('transactionDelaySeconds', 'number') : <Mono>{item.transactionDelaySeconds}s</Mono>}</Td>
                  <Td>{isEditing ? field('transactionsExpirationSeconds', 'number') : <Mono>{item.transactionsExpirationSeconds}s</Mono>}</Td>
                  <Td>{isEditing ? field('missingTxMinutesTimeout', 'number') : <Mono>{item.missingTxMinutesTimeout}m</Mono>}</Td>
                  <Td>{isEditing ? field('minimumMinutesBetweenExecutions', 'number') : <Mono>{item.minimumMinutesBetweenExecutions}m</Mono>}</Td>
                  <Td>{isEditing ? field('maxPriorityFeePerGas', 'text') : <Mono>{item.maxPriorityFeePerGas ?? '—'}</Mono>}</Td>
                  <Td>{isEditing ? field('inactiveMinutesToNotify', 'number') : <Mono>{item.inactiveMinutesToNotify || 0}m</Mono>}</Td>
                  <Td>{isEditing ? field('executorBalanceThresholdUsd', 'number') : <Mono>${item.executorBalanceThresholdUsd || 0}</Mono>}</Td>
                  <Td>{isEditing ? field('explorerUrl', 'text', 'xwide') : <SubUrl>{item.explorerUrl || '—'}</SubUrl>}</Td>
                  <Td>
                    {isEditing ? (
                      <BtnGroup>
                        <SaveBtn onClick={handleSaveEdit}>Save</SaveBtn>
                        <GhostBtn onClick={() => setEditIndex(null)}>Cancel</GhostBtn>
                      </BtnGroup>
                    ) : (
                      <EditBtn onClick={() => handleEdit(item, index)}>Edit</EditBtn>
                    )}
                  </Td>
                  <Td>
                    <DeleteBtn onClick={() => setDeleteParams(item)}>Delete</DeleteBtn>
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </TableWrap>

      {deleteParams !== null && (
        <CustomConfirmationModal
          message="Are you sure you want to delete this network?"
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteParams(null)}
        />
      )}
    </Wrap>
  )
}

export default NetworksPage

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`display: flex; flex-direction: column; gap: 12px;`
const Header = styled.div`display: flex; justify-content: flex-end;`

const shimmer = keyframes`0% { transform: translateX(-100%); } 100% { transform: translateX(100%); }`
const LoaderBar = styled.div`
  height: 2px; background: rgba(255,255,255,0.06); border-radius: 2px; overflow: hidden; position: relative;
  &::after { content: ''; display: block; position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(111,92,230,0.5), transparent); animation: ${shimmer} 1.4s ease infinite; }
`

const TableWrap = styled.div`overflow-x: auto; border: 1px solid rgba(255,255,255,0.07); border-radius: 8px;`
const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`
const Th = styled.th`padding: 10px 12px; text-align: left; color: #a1a1a1; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; border-bottom: 1px solid rgba(255,255,255,0.06);`
const Td = styled.td`padding: 8px 10px; color: #a0a0a0; font-size: 13px; vertical-align: middle; border-bottom: 1px solid rgba(255,255,255,0.04); tr:last-child & { border-bottom: none; }`
const Mono = styled.span`font-family: 'SF Mono','Fira Code',monospace; font-size: 12px; color: #a0a0a0;`
const Muted = styled.span`color: #777;`
const SubUrl = styled.span`font-family: 'SF Mono','Fira Code',monospace; font-size: 10px; color: #777; word-break: break-all;`

const DeniedBadge = styled.span`display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 4px; font-size: 11px; background: rgba(255,68,68,0.1); color: #ff4444; border: 1px solid rgba(255,68,68,0.25);`

const InlineInput = styled.input`
  background: #0d0d0d; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px;
  color: #ededed; font-size: 12px; padding: 0 6px; height: 26px; outline: none;
  &:focus { border-color: rgba(111,92,230,0.5); }
`

const BtnGroup = styled.div`display: flex; gap: 4px;`
const ActionBtn = styled.button`height: 26px; padding: 0 9px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 4px; color: #a0a0a0; font-size: 11px; cursor: pointer; transition: border-color 0.15s, color 0.15s;`
const EditBtn = styled(ActionBtn)`&:hover { border-color: rgba(111,92,230,0.4); color: #a89aff; }`
const SaveBtn = styled(ActionBtn)`&:hover { border-color: rgba(51,194,176,0.4); color: #33C2B0; }`
const GhostBtn = styled(ActionBtn)``
const DeleteBtn = styled(ActionBtn)`&:hover { border-color: rgba(255,68,68,0.4); color: #ff4444; }`

const AddBtn = styled.button`
  height: 28px; padding: 0 12px; background: rgba(111,92,230,0.1); border: 1px solid rgba(111,92,230,0.3);
  border-radius: 5px; color: #a89aff; font-size: 12px; font-weight: 500; cursor: pointer;
  transition: background 0.15s; &:hover { background: rgba(111,92,230,0.2); border-color: rgba(111,92,230,0.5); }
`
